/**
 * QuikSlides — "turn business information into better slides" page.
 * All copy lives here; sections only handle layout and motion.
 */

import { LINKS } from "./quikit";

export const QSL = {
  meta: {
    title: "Online presentations & business decks — QuikSlides by Quikit",
    description:
      "Turn ideas, data, plans and business information into professional presentations your audience can understand, remember and act on — included with your Quikit product.",
  },
  links: { register: LINKS.register },
};

/* ------------------------------------------------------------------ */
/*  Hero                                                               */
/* ------------------------------------------------------------------ */
export const QSL_HERO = {
  eyebrow: "Turn business information into better slides",
  title: "Create. Design.\nCollaborate. Present.",
  sub: "Turn ideas, data, plans and business information into professional presentations your audience can understand, remember and act on.",
  lead: "Create, collaborate and present in one workspace — included with your Quikit product at no additional cost.",
  foot: "No credit card required. Up and running in minutes.",
  link: { label: "See QuikSlides in action", href: "#how" },
};

/** The deck mock shown in the hero. */
export const QSL_PULSE = {
  path: "Workspace → Slides → Q3 board review",
  deck: { name: "Q3 board review", meta: "14 slides · shared · linked to QuikFinance" },
  collaborators: [
    { name: "Ishaan", state: "Editing" as const },
    { name: "Leela", state: "Commenting" as const },
    { name: "Omar", state: "Viewing" as const },
  ],
  slides: [
    { n: "01", title: "Where we landed", kind: "Title" },
    { n: "02", title: "Revenue vs plan", kind: "Chart", live: true },
    { n: "03", title: "Pipeline by region", kind: "Chart", live: true },
    { n: "04", title: "What we're asking for", kind: "Decision" },
  ],
  source: { label: "Pulled from", items: ["QuikFinance · Q3 actuals", "QuikCRM · pipeline"] },
  result: { label: "Deck refreshed", value: "2 charts updated" },
  footer: "Both charts re-pulled from the source — no copy-paste, no stale numbers.",
};

/* ------------------------------------------------------------------ */
/*  The problem                                                        */
/* ------------------------------------------------------------------ */
export const QSL_PROBLEM = {
  eyebrow: "The problem",
  // Too long for the column on one line — even the second sentence alone overflows, so both
  // breaks are placed rather than left to chance.
  title: "Your business has the information.\nTurning it into a great presentation\ntakes too much work.",
  text: "Sales decks, board reviews, business plans — every business builds presentations. Turning scattered information into a story takes too long.",
  items: [
    {
      title: "Too much time designing",
      text: "Teams spend hours adjusting layouts, formatting slides and making presentations look professional.",
    },
    {
      title: "Information is scattered",
      text: "The numbers, insights and content you need live across documents, spreadsheets, CRM systems and other tools.",
    },
    {
      title: "Presentations go stale",
      text: "When the underlying information changes, decks quickly become outdated — usually without anyone noticing.",
    },
    {
      title: "Too many versions",
      text: "Multiple people editing and sharing copies creates confusion about which presentation is actually final.",
    },
    {
      title: "Collaboration gets messy",
      text: "Feedback moves between email, chat and documents instead of staying connected to the presentation.",
    },
    {
      title: "Another subscription",
      text: "Businesses already pay for tools to manage their work. Presentation software becomes one more to manage.",
    },
  ],
  result: {
    kicker: "The real problem",
    title: "The problem isn’t making slides. It’s turning business information into a clear story.",
    text: "The work is already done. What takes the time is making it understandable to someone else.",
    cta: "See how QuikSlides works",
  },
};

/* ------------------------------------------------------------------ */
/*  The shift                                                          */
/* ------------------------------------------------------------------ */
export const QSL_SHIFT = {
  eyebrow: "The shift",
  title: "Stop building presentations from scratch. Start turning your work into stories.",
  text: "Traditional presentation work runs find data, copy, paste, design, review, fix, rebuild, present. QuikSlides makes that workflow considerably shorter.",
  /** Each row is a genuine swap — no term appears on both sides of the arrow. */
  rows: [
    { from: "Find the data", to: "Create" },
    { from: "Copy and paste", to: "Structure" },
    { from: "Format every slide", to: "Design" },
    { from: "Email for feedback", to: "Collaborate" },
    { from: "Rebuild it again", to: "Present" },
  ],
  questionsLead: "Your business already has the information. QuikSlides helps your team turn it into something people can act on:",
  questions: ["From data to story", "From ideas to slides", "From work to presentation"],
};

/* ------------------------------------------------------------------ */
/*  How it works                                                       */
/* ------------------------------------------------------------------ */
export const QSL_STEPS = {
  eyebrow: "Meet QuikSlides",
  title: "Your presentation workspace, built into Quikit.",
  text: "One place to create professional presentations, structure information, collaborate with teammates and present with confidence.",
  steps: [
    {
      kicker: "Create",
      title: "An editor built for everyday business use",
      text: "Build presentations with an intuitive editor so your team can focus on the message rather than the mechanics of the tool.",
    },
    {
      kicker: "Design",
      title: "Clear, structured, visually engaging slides",
      text: "Turn information into slides that hold together visually, without spending the afternoon nudging boxes into alignment.",
    },
    {
      kicker: "Collaborate",
      title: "One presentation, not five copies",
      text: "Work with your team on the same presentation instead of merging versions and guessing which one is current.",
    },
    {
      kicker: "Present",
      title: "From editing to presenting",
      text: "Move into a dedicated presentation experience when the deck is ready, without switching to another application.",
    },
    {
      kicker: "Share",
      title: "The right deck to the right people",
      text: "Get the presentation in front of teammates, clients and stakeholders, with control over who sees what.",
    },
    {
      kicker: "Connect",
      title: "Built around work already happening",
      text: "Build presentations around the information already living across Quikit, rather than re-gathering it by hand.",
    },
  ],
  cta: "Start creating for free",
};

/* ------------------------------------------------------------------ */
/*  Everything you need                                                */
/* ------------------------------------------------------------------ */
export const QSL_EVERYTHING = {
  eyebrow: "Everything you need",
  title: "Everything you need to build better presentations.",
  text: "The essential tools for creating, refining and presenting business decks — all included with your Quikit product.",
  items: [
    { title: "Slide editor", text: "An intuitive editing experience so your team can focus on the message, not the mechanics." },
    { title: "Themes & layouts", text: "Structured layouts and themes for polished slides without designing every page." },
    { title: "Templates", text: "Reusable structures for common business presentations, so you start faster." },
    { title: "Rich content", text: "Text, visuals, tables, charts and more, to make complex information easier to understand." },
    { title: "Business data in slides", text: "Bring the numbers and information your business already has into a deck, instead of hunting and pasting." },
    { title: "Collaboration", text: "Bring teammates into the same presentation instead of merging versions later." },
    { title: "Comments & feedback", text: "Review slides and discuss ideas without moving the conversation to another application." },
    { title: "Presentation mode", text: "Move seamlessly from editing to presenting when it’s time to deliver your story." },
    { title: "Sharing", text: "Share with teammates, clients, stakeholders and collaborators when you need to." },
  ],
  close: "One workspace from first slide to final presentation.",
};

/* ------------------------------------------------------------------ */
/*  From information to story                                          */
/* ------------------------------------------------------------------ */
export const QSL_STORY = {
  eyebrow: "From information to story",
  title: "Great presentations don’t just show information. They make it understandable.",
  text: "A spreadsheet gives you numbers. A document gives you details. A presentation gives your audience the story behind them.",
  steps: [
    { name: "Bring it together", text: "Pull together the information your audience actually needs." },
    { name: "Structure it", text: "Organize the content into a logical narrative." },
    { name: "Visualize it", text: "Use charts, tables, visuals and layouts to make it easier to understand." },
    { name: "Tell the story", text: "Turn individual pieces of information into one coherent message." },
    { name: "Drive action", text: "End with a clear takeaway, decision or next step." },
  ],
  close: "Don’t just show the data. Tell people what it means.",
};

/* ------------------------------------------------------------------ */
/*  Presentation types                                                 */
/* ------------------------------------------------------------------ */
export const QSL_TYPES = {
  eyebrow: "Presentation types",
  title: "One workspace. Every business presentation.",
  text: "Whatever you’re presenting, it starts in the same place.",
  items: [
    { title: "Sales presentations", text: "Turn customer insights, product information and proposals into compelling sales decks." },
    { title: "Investor & board decks", text: "Bring performance, strategy, financials and plans into one structured presentation." },
    { title: "Project reviews", text: "Show progress, milestones, challenges, results and next steps." },
    { title: "Business plans", text: "Turn strategy, goals and operating plans into a clear business story." },
    { title: "Marketing presentations", text: "Present campaign strategies, performance, research and ideas." },
    { title: "Training & onboarding", text: "Turn knowledge and processes into engaging learning material." },
    { title: "Product presentations", text: "Explain products, features, roadmaps and launches clearly." },
    { title: "Internal meetings", text: "Turn updates, decisions and discussions into structured presentations." },
    { title: "Reports & reviews", text: "Transform business information into presentations that are easier to consume." },
  ],
  close: "Whatever you’re presenting, start with QuikSlides.",
};

/* ------------------------------------------------------------------ */
/*  Templates, design and collaboration                                */
/* ------------------------------------------------------------------ */
export const QSL_WORK = {
  eyebrow: "Templates, design & collaboration",
  title: "One deck. One team. One version.",
  text: "Three things slow a deck down: starting from blank, formatting it by hand, and merging everyone’s copy at the end. All three live in the same workspace.",
  blocks: [
    {
      kicker: "Templates",
      title: "Don’t start with a blank slide",
      text: "Starting from scratch slows teams down. Reusable structures let you focus on the message instead of rebuilding the same layouts every time.",
      items: ["Sales decks", "Business reviews", "Project updates", "Strategy decks", "Training decks", "Company presentations"],
    },
    {
      kicker: "Design",
      title: "Professional slides without being a designer",
      text: "Not every employee is a presentation designer, and they shouldn’t need to be. Your team brings the expertise; QuikSlides helps bring it to the screen.",
      items: ["Structured layouts", "Reusable themes", "Rich content", "Consistent formatting", "Templates"],
    },
    {
      kicker: "Collaboration",
      title: "Stop merging decks",
      text: "Presentation collaboration usually becomes a version-control problem — deck-final, deck-final-v2, deck-final-FINAL. Build it together instead.",
      items: ["Create together", "Review together", "Edit together", "Feedback connected", "Share the current version"],
    },
  ],
  close: "Less formatting. More storytelling.",
};

/* ------------------------------------------------------------------ */
/*  Presentation mode                                                  */
/* ------------------------------------------------------------------ */
export const QSL_PRESENT = {
  eyebrow: "Presentation mode",
  title: "From editing to presenting — without the friction.",
  text: "When the deck is ready, the workspace should get out of the way — built for the rooms you present in.",
  chain: [
    { name: "Prepare", text: "Build and refine your slides." },
    { name: "Present", text: "Move into presentation mode." },
    { name: "Communicate", text: "Focus on your audience instead of the tool." },
    { name: "Act", text: "End with the decision your presentation is designed to drive." },
  ],
  rooms: [
    "Client meetings",
    "Sales pitches",
    "Board meetings",
    "Investor discussions",
    "Project reviews",
    "Team meetings",
    "Training sessions",
    "Business reviews",
  ],
  close: "Your slides are ready. Now tell the story.",
  cta: "Start creating for free",
};

/* ------------------------------------------------------------------ */
/*  Built for every team                                               */
/* ------------------------------------------------------------------ */
export const QSL_TEAMS = {
  eyebrow: "Built for every team",
  title: "Everyone has something to present.",
  text: "QuikSlides isn’t just for sales or marketing — every team needs to communicate ideas, information and decisions.",
  groups: [
    { name: "CEOs & founders", items: ["Strategy", "Investor updates", "Business reviews", "Company plans"] },
    { name: "Sales", items: ["Sales pitches", "Proposals", "Account reviews", "Product presentations"] },
    { name: "Marketing", items: ["Campaigns", "Strategies", "Performance reviews", "Creative concepts"] },
    { name: "Product", items: ["Roadmaps", "Launches", "Product updates", "Product strategy"] },
    { name: "HR", items: ["Training", "Onboarding", "Workforce reviews", "People initiatives"] },
    { name: "Finance", items: ["Financial reviews", "Forecasts", "Budgets", "Management reports"] },
    { name: "Operations", items: ["Process updates", "Project reviews", "Operational performance", "Planning"] },
    { name: "Leadership", items: ["Board presentations", "Strategic reviews", "Organizational updates", "Decisions"] },
  ],
  close: "One presentation workspace for the entire organization.",
};

/* ------------------------------------------------------------------ */
/*  Connected to your business                                         */
/* ------------------------------------------------------------------ */
export const QSL_ECOSYSTEM = {
  eyebrow: "Connected to your business",
  title: "Your presentations shouldn’t live\noutside your business.",
  text: "A sales deck relates to a customer. A project review relates to a project. The work happens across Quikit.",
  groups: [
    { name: "QuikCRM", items: ["Customer information", "Sales presentations", "Account reviews"] },
    { name: "QuikProjects", items: ["Progress and milestones", "Results", "Stakeholder presentations"] },
    { name: "QuikFinance", items: ["Financial information", "Business reviews", "Planning decks"] },
    { name: "QuikInsights", items: ["Business insights", "Analytics", "Decisions leaders can act on"] },
    { name: "QuikScale", items: ["Strategy", "Planning", "Growth initiatives"] },
    { name: "QuikPeople", items: ["HR presentations", "Workforce reviews", "Leadership updates"] },
    { name: "QuikDocs", items: ["Detailed documentation", "Condensed into a story", "Supporting detail"] },
    { name: "QuikSocial & QuikSEO", items: ["Marketing performance", "Campaigns", "Growth results"] },
  ],
  close: "The work happens across Quikit. QuikSlides helps you tell the story.",
};

/* ------------------------------------------------------------------ */
/*  Why QuikSlides                                                     */
/* ------------------------------------------------------------------ */
export const QSL_WHY = {
  eyebrow: "Why QuikSlides",
  title: "Creating slides is easy. Connecting them to your business is the difference.",
  text: "Create better slides. Tell better stories.",
  items: [
    { title: "Everything you need to create", text: "Editor, layouts, templates, rich content and presentation mode in one workspace." },
    { title: "Built for collaboration", text: "Create, review and refine presentations together rather than in parallel copies." },
    { title: "Designed for business", text: "Decks for sales, strategy, projects, finance, marketing, HR and leadership." },
    { title: "Templates that save time", text: "Start from proven structures instead of an empty canvas." },
    { title: "Present with confidence", text: "Move from creation to presentation without switching tools." },
    { title: "Connected to your business", text: "Keep presentations closer to the work and information behind them." },
    { title: "Part of Quikit", text: "QuikSlides works alongside the wider Quikit ecosystem rather than beside it." },
    { title: "No extra subscription", text: "QuikSlides is included with your Quikit product at no additional cost." },
  ],
};

/* ------------------------------------------------------------------ */
/*  Compare                                                            */
/* ------------------------------------------------------------------ */
export type CompareCell = boolean | string;
export const QSL_COMPARE = {
  eyebrow: "QuikSlides vs standalone presentation tools",
  title: "Why add another subscription for slides your business already needs?",
  text: "The editor and the templates are table stakes. What differs is whether the deck sits inside your business — and whether you pay separately for it.",
  columns: ["QuikSlides", "Standalone tool"],
  rows: [
    { feature: "Presentation editor", cells: [true, true] as CompareCell[] },
    { feature: "Templates", cells: [true, true] as CompareCell[] },
    { feature: "Themes & layouts", cells: [true, true] as CompareCell[] },
    { feature: "Rich content", cells: [true, true] as CompareCell[] },
    { feature: "Charts & tables", cells: [true, true] as CompareCell[] },
    { feature: "Collaboration", cells: [true, true] as CompareCell[] },
    { feature: "Comments & feedback", cells: [true, true] as CompareCell[] },
    { feature: "Presentation mode", cells: [true, true] as CompareCell[] },
    { feature: "Sharing", cells: [true, true] as CompareCell[] },
    { feature: "Business ecosystem", cells: ["Quikit", "Standalone"] as CompareCell[] },
    { feature: "Quikit integration", cells: [true, false] as CompareCell[] },
    { feature: "Connected workflows", cells: [true, "Limited"] as CompareCell[] },
    { feature: "Additional subscription", cells: ["$0", "Required"] as CompareCell[] },
  ],
  close: {
    title: "Two ways to build a deck.",
    options: [{ name: "A standalone presentation tool", text: "Helps you make slides — on its own subscription, away from the work they describe." }],
    product: { name: "QuikSlides", text: "Helps you turn business work into presentations, inside the same ecosystem." },
  },
};

/* ------------------------------------------------------------------ */
/*  The business case                                                  */
/* ------------------------------------------------------------------ */
export const QSL_CASE = {
  eyebrow: "The business case",
  title: "Why pay separately for presentation software?",
  text: "You already pay for software to manage customers, projects, people and finance. Presentation software is another place to move it.",
  manual: {
    kicker: "A separate presentation subscription",
    title: "Another per-user licence, away from the work",
    items: ["Its own bill", "Its own admin", "Its own identity", "Copied-in numbers", "No business context", "Another place to look"],
    value: "Separate",
    unit: "per-user subscription",
  },
  product: {
    kicker: "QuikSlides",
    title: "Included with any Quikit product",
    text: "Creating presentations, templates and layouts, collaboration, presentation mode and sharing are built into the ecosystem rather than sold alongside it.",
    close: "One less subscription. One more connected workspace.",
    value: "$0",
    unit: "additional per user",
  },
  /** The four figures come from the brief's own reported numbers, presented as reported. */
  proof: {
    label: "Reported on the product page",
    stats: [
      { value: "10K+", text: "presentations created" },
      { value: "2.5K+", text: "active teams" },
      { value: "50K+", text: "slides created" },
      { value: "$0", text: "additional cost with Quikit" },
    ],
    note: "Product-reported adoption figures. Results and usage vary by organization.",
  },
};

/* ------------------------------------------------------------------ */
/*  Plans                                                              */
/* ------------------------------------------------------------------ */
/** Every figure here comes from the brief — the trial, $0 included, the $1 add-on and Enterprise. */
export const QSL_PLANS = {
  eyebrow: "Pricing",
  title: "Professional presentations.\nWithout another subscription.",
  text: "No separate presentation subscription. No additional per-user fee.",
  plans: [
    {
      name: "Free",
      price: "$0",
      unit: "for 14 days",
      text: "Experience the full QuikSlides product before you commit, with every feature unlocked.",
      lead: "",
      cta: "Start free trial",
      ctaHref: LINKS.register,
      foot: "No credit card required",
      features: [
        "Every QuikSlides feature unlocked",
        "Slide editor",
        "Themes & layouts",
        "Templates",
        "Rich content",
        "Collaboration",
        "Comments & feedback",
        "Presentation mode",
        "Sharing",
      ],
    },
    {
      name: "Premium",
      price: "$0",
      unit: "included with Quikit",
      text: "Included with any Quikit product — no additional presentation subscription.",
      lead: "Everything in Free, plus:",
      featured: true,
      badge: "Included",
      cta: "Get QuikSlides",
      ctaHref: LINKS.register,
      foot: "No separate presentation licence",
      features: [
        "Reusable template library",
        "Connections with Quikit products",
        "Available across your organization",
        "Full REST API",
        "Email & chat support",
      ],
    },
    {
      name: "Pro",
      price: "$1",
      unit: "/ product / user / month",
      text: "Add other Quikit applications to the same workspace as your business grows.",
      lead: "Everything in Premium, plus:",
      cta: "Explore Quikit",
      ctaHref: "/#pricing",
      foot: "Add or remove products as you grow",
      features: [
        "One login across products",
        "Shared data",
        "One AI layer",
        "Add or remove products as you grow",
        "Email & chat support",
      ],
    },
    {
      name: "Enterprise",
      price: "Custom",
      unit: "",
      text: "For larger organizations and bespoke requirements.",
      lead: "",
      cta: "Talk to sales",
      ctaHref: "#demo",
      foot: "Tailored to your organization",
      features: [
        "Dedicated account manager",
        "Migration & onboarding support",
        "Custom deployment options",
        "Priority support",
        "API & integration support",
      ],
    },
  ],
};

/* ------------------------------------------------------------------ */
/*  FAQ                                                                */
/* ------------------------------------------------------------------ */
export const QSL_FAQ = [
  {
    q: "What is QuikSlides?",
    a: "QuikSlides is Quikit’s online presentation creation and collaboration workspace. It helps teams create, design, collaborate on, present and share business presentations.",
  },
  {
    q: "Why was QuikPresentation renamed to QuikSlides?",
    a: "QuikSlides is the new product name for QuikPresentation. The name more directly reflects the core experience of creating, designing and presenting slides.",
  },
  {
    q: "Is QuikSlides free?",
    a: "QuikSlides is included at no additional cost with any Quikit product.",
  },
  {
    q: "Do I need a separate subscription?",
    a: "No. QuikSlides is included with your Quikit product.",
  },
  {
    q: "Is QuikSlides like Google Slides?",
    a: "QuikSlides provides an online presentation creation and collaboration experience similar to modern presentation tools, while being part of the wider Quikit ecosystem.",
  },
  {
    q: "Is QuikSlides like PowerPoint?",
    a: "QuikSlides provides presentation editing and presentation capabilities designed for professional business slides. Specific feature parity with PowerPoint depends on the capabilities available in the current product.",
  },
  {
    q: "What can I create with QuikSlides?",
    a: "Sales pitches, project reviews, board presentations, business plans, marketing presentations, training decks, strategy presentations, reports and other business presentations.",
  },
  {
    q: "Can teams collaborate on presentations?",
    a: "Yes. QuikSlides supports collaborative workflows so teams can create, review and refine presentations together.",
  },
  {
    q: "Does QuikSlides have templates?",
    a: "Yes. QuikSlides includes reusable presentation structures to help teams start faster.",
  },
  {
    q: "Can I use charts, tables and visuals?",
    a: "Yes. QuikSlides supports rich presentation content including text, visuals, tables and charts.",
  },
  {
    q: "Can I present directly from QuikSlides?",
    a: "Yes. QuikSlides includes a dedicated presentation mode for delivering completed presentations.",
  },
  {
    q: "Can I share presentations?",
    a: "Yes. Presentations can be shared with teammates, clients, stakeholders and collaborators.",
  },
  {
    q: "Who should use QuikSlides?",
    a: "Anyone who creates or presents business information — founders, leadership, sales, marketing, finance, HR, product and operations teams.",
  },
  {
    q: "Can QuikSlides work with other Quikit products?",
    a: "Yes. QuikSlides is part of the Quikit ecosystem and works alongside other Quikit applications.",
  },
  {
    q: "Why use QuikSlides instead of another presentation tool?",
    a: "It combines presentation creation and collaboration with the wider Quikit business ecosystem, and is included with your Quikit product rather than requiring another subscription.",
  },
];

/* ------------------------------------------------------------------ */
/*  Demo                                                               */
/* ------------------------------------------------------------------ */
export type QslIntent = "consolidate" | "decks" | "evaluate";
export const QSL_DEMO = {
  agenda: [
    { title: "A deck from your data", text: "A board review built from real numbers rather than copied screenshots." },
    { title: "The version problem, gone", text: "One deck, several editors, no merging at the end of it." },
    { title: "The subscription you drop", text: "What your presentation tool costs today against $0 with Quikit." },
  ],
  intents: [
    { value: "consolidate" as QslIntent, label: "Drop our deck tool", meta: "We pay for one today" },
    { value: "decks" as QslIntent, label: "Standardize decks", meta: "Every deck looks different" },
    { value: "evaluate" as QslIntent, label: "Just evaluating", meta: "Exploring options" },
  ],
  copy: {
    consolidate: {
      eyebrow: "Drop the extra subscription",
      title: "Let’s replace your presentation tool.",
      lead: "Tell us what your team uses today and how many people are on it. We’ll show you what QuikSlides covers at no additional cost.",
      button: "Show me the comparison",
      ok: "Request received",
      msg: "A QuikSlides specialist will contact you with the comparison.",
    },
    decks: {
      eyebrow: "Standardize your decks",
      title: "Let’s build your template set.",
      lead: "Tell us which decks your team rebuilds most often. We’ll show you how QuikSlides would turn them into reusable structures.",
      button: "Plan my templates",
      ok: "Request received",
      msg: "A QuikSlides specialist will contact you to plan your template set.",
    },
    evaluate: {
      eyebrow: "Evaluate QuikSlides",
      title: "Comparing presentation tools?",
      lead: "Tell us how your team builds decks today — a presentation suite, shared drives, or a designer. We’ll show you how QuikSlides would fit.",
      button: "Book my walkthrough",
      ok: "Request received",
      msg: "A QuikSlides specialist will contact you to set up your walkthrough.",
    },
  } satisfies Record<QslIntent, { eyebrow: string; title: string; lead: string; button: string; ok: string; msg: string }>,
  sizes: ["1–10", "11–50", "51–200", "201–500", "500+"],
  current: ["Google Slides", "Microsoft PowerPoint", "Keynote", "Canva", "A design team", "Nothing formal yet"],
};

/* ------------------------------------------------------------------ */
/*  Closing                                                            */
/* ------------------------------------------------------------------ */
export const QSL_CTA = {
  eyebrow: "Turn your business work into a story worth presenting",
  title: "Create. Collaborate. Present.",
  text: "Create professional slides, collaborate with your team and keep presentations connected to the business behind them.",
  steps: ["Create", "Structure", "Design", "Collaborate", "Present", "Share"],
  foot: "No credit card required. Included with your Quikit product.",
};
